import React, { useState } from 'react';
import ToolLayout from '../components/ToolLayout';

const XmlFormatter: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const formatXml = () => {
    setError('');
    setSuccess('');
    
    if (!input.trim()) {
      setError('Vui lòng nhập XML để format');
      return;
    }

    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(input.trim(), 'text/xml');
      
      // Check for parsing errors
      const parseError = xmlDoc.getElementsByTagName('parsererror');
      if (parseError.length > 0) {
        setError('XML không hợp lệ: ' + parseError[0].textContent);
        setOutput('');
        return;
      }

      const formatted = formatXmlString(xmlDoc);
      setOutput(formatted);
      setSuccess('XML đã được format thành công! ✅');
    } catch (err) {
      setError('Không thể format XML: ' + (err as Error).message);
      setOutput('');
    }
  };

  const minifyXml = () => {
    setError('');
    setSuccess('');
    
    if (!input.trim()) {
      setError('Vui lòng nhập XML để minify');
      return;
    }

    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(input.trim(), 'text/xml');
      
      // Check for parsing errors
      const parseError = xmlDoc.getElementsByTagName('parsererror');
      if (parseError.length > 0) {
        setError('XML không hợp lệ: ' + parseError[0].textContent);
        setOutput('');
        return;
      }

      const minified = minifyXmlString(xmlDoc);
      setOutput(minified);
      setSuccess('XML đã được minify thành công! ✅');
    } catch (err) {
      setError('Không thể minify XML: ' + (err as Error).message);
      setOutput('');
    }
  };

  const validateXml = () => {
    setError('');
    setSuccess('');
    
    if (!input.trim()) {
      setError('Vui lòng nhập XML để validate');
      return;
    }

    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(input.trim(), 'text/xml');
      
      // Check for parsing errors
      const parseError = xmlDoc.getElementsByTagName('parsererror');
      if (parseError.length > 0) {
        setError('XML không hợp lệ: ' + parseError[0].textContent);
        setOutput('');
        return;
      }

      setSuccess('XML hợp lệ! ✓');
      setOutput('');
    } catch (err) {
      setError('XML không hợp lệ: ' + (err as Error).message);
      setOutput('');
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
    setSuccess('');
  };

  const copyToClipboard = async () => {
    if (output) {
      try {
        await navigator.clipboard.writeText(output);
        setSuccess('Đã copy vào clipboard!');
      } catch (err) {
        setError('Không thể copy vào clipboard');
      }
    }
  };

  // Format XML with proper indentation
  const formatXmlString = (xmlDoc: Document): string => {
    const serializer = new XMLSerializer();
    const xmlString = serializer.serializeToString(xmlDoc);
    return prettifyXml(xmlString);
  };

  // Minify XML by removing whitespace
  const minifyXmlString = (xmlDoc: Document): string => {
    const serializer = new XMLSerializer();
    return serializer.serializeToString(xmlDoc).replace(/>\s+</g, '><').trim();
  };

  // Pretty print XML with indentation
  const prettifyXml = (xml: string): string => {
    const PADDING = '  '; // 2 spaces
    const reg = /(>)(<)(\/*)/g;
    let pad = 0;

    xml = xml.replace(reg, '$1\n$2$3');

    return xml.split('\n').map((node) => {
      let indent = '';
      if (node.match(/.+<\/\w[^>]*>$/)) {
        // Node with content
        indent = '';
      } else if (node.match(/^<\/\w/) && pad > 0) {
        // Closing tag
        pad -= 1;
      } else if (node.match(/^<\w[^>]*[^\/]>.*$/)) {
        // Opening tag
        indent = '';
      } else {
        indent = '';
      }

      for (let i = 0; i < pad; i++) {
        indent += PADDING;
      }

      if (node.match(/^<\w[^>]*[^\/]>.*$/)) {
        pad += 1;
      }

      return indent + node;
    }).join('\n');
  };

  return (
    <ToolLayout 
      title="XML Formatter" 
      description="Format, validate, và beautify XML documents một cách dễ dàng"
    >
      <div className="input-group">
        <label className="input-label">Nhập XML:</label>
        <textarea
          className="input-textarea"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={'Nhập XML của bạn ở đây...\n\nVí dụ:\n<root>\n  <item>value</item>\n</root>'}
          style={{ fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace' }}
        />
      </div>

      <div className="button-group">
        <button className="action-button" onClick={formatXml}>
          🎨 Format XML
        </button>
        <button className="action-button" onClick={minifyXml}>
          📦 Minify XML
        </button>
        <button className="action-button" onClick={validateXml}>
          ✅ Validate XML
        </button>
        <button className="action-button secondary" onClick={clearAll}>
          Clear All
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {output && (
        <div className="input-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label className="input-label">Kết quả:</label>
            <button className="action-button" onClick={copyToClipboard}>
              📋 Copy
            </button>
          </div>
          <textarea
            className="input-textarea"
            value={output}
            readOnly
            style={{ 
              fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
              textAlign: 'left'
            }}
          />
        </div>
      )}

      {/* Info Section */}
      <div className="xml-info-section">
        <h4 className="xml-info-title">
          🛠️ XML Tools:
        </h4>
        <div className="xml-info-content">
          <p>
            <strong>🎨 Format XML:</strong> Beautify XML với proper indentation
          </p>
          <p>
            <strong>📦 Minify XML:</strong> Loại bỏ whitespace và compress XML
          </p>
          <p>
            <strong>✅ Validate XML:</strong> Kiểm tra tính hợp lệ của XML syntax
          </p>

          <strong>Tính năng:</strong>
          <ul className="xml-info-list">
            <li>Hỗ trợ tất cả XML tags và attributes</li>
            <li>Tự động detect và báo lỗi syntax</li>
            <li>Pretty formatting với 2-space indentation</li>
            <li>Copy to clipboard functionality</li>
          </ul>
        </div>
      </div>

      <style>{`
        .xml-info-section {
          margin-top: 2rem;
          padding: 1.5rem;
          background: var(--bg-secondary);
          border-radius: 12px;
          border: 2px solid var(--accent-primary);
        }

        .xml-info-title {
          margin: 0 0 1rem 0;
          color: var(--accent-primary);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
        }

        .xml-info-content {
          color: var(--text-secondary);
          font-size: 0.9rem;
          line-height: 1.6;
        }

        .xml-info-content p {
          margin: 0 0 0.5rem 0;
        }

        .xml-info-list {
          margin: 0.5rem 0;
          padding-left: 1.5rem;
        }
      `}</style>
    </ToolLayout>
  );
};

export default XmlFormatter;






